<template>
    <form>
        <div class="form-group mb-3">
            <label for="description">{{ __('manage_poi.fields.name') }}</label>
            <golia-input id="description" :value="localPoi.description" v-model="localPoi.description"></golia-input>
        </div>

        <div class="row">
            <div class="col-md-6">
                <div class="form-group mb-3">
                    <label for="type">{{ __('manage_poi.fields.type') }}</label>
                    <golia-input id="type" type="select" :options="poiTypes" :value="localPoi.type"
                        v-model="localPoi.type"></golia-input>
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group mb-3">
                    <label for="type">{{ __('manage_poi.fields.color') }}</label>
                    <div class="field-wrapper">
                        <input id="color" type="color" class="form-control" v-model="localPoi.color" />
                    </div>
                </div>
            </div>
        </div>

        <div class="form-group mb-3">
            <label for="type">{{ __('manage_poi.fields.note') }}</label>
            <textarea id="notes" class="form-control" rows="3" v-model="localPoi.note"></textarea>
        </div>

        <div class="form-group mb-3">
            <label for="address">{{ __('address') }}</label>
            <p>
                <!-- address is a plain string for saved POIs, or a
                     { label, referenceCoordinate } object while picking one -->
                {{ (localPoi.address && localPoi.address.label) || localPoi.address }}
            </p>
        </div>

        <div class="form-group mb-3">
            <label for="address">{{ __('manage_poi.fields.address') }}</label>
            <golia-waypoint-search id="address" v-model="localPoi.address" />
            <b-alert class="select-on-map p-2 mt-2" variant="primary" show
                v-if="localPoi.address && (!localPoi.lat || !localPoi.lng)">
                {{ __('planner.select_on_map') }}
            </b-alert>
        </div>

        <div class="form-group mb-3">
            <label for="areaType">{{ __('manage_poi.fields.area_type') }}</label>
            <golia-input id="areaType" type="select" :options="areaTypes" :value="localPoi.area_type"
                v-model="localPoi.area_type"></golia-input>
        </div>

        <div class="form-group mb-3" v-if="localPoi.area_type !== 'polygon'">
            <label for="radius">{{ __('manage_poi.fields.radius') }} (m)</label>
            <golia-input id="radius" type="number" :value="localPoi.radius"
                v-model.number="localPoi.radius"></golia-input>

            <small class="form-text text-muted">
                {{ __('manage_poi.fields.radius_description') }}
            </small>
        </div>

        <div class="form-group mb-3" v-else>
            <small class="form-text text-muted">
                {{ __('manage_poi.fields.polygon_description') }}
            </small>
        </div>

        <div class="d-flex gap-2 align-items-center border border rounded-3 p-3" v-if="localPoi.id">
            <i class="fa-fw fa-solid fa-bell"></i>
            <div class="d-flex gap-2">
                <div class="form-check">
                    <input type="checkbox" class="form-check-input" :id="'entry-' + localPoi.id"
                        v-model="localPoi.alert.notification_events" value="entry" />
                    <label class="form-check-label" :for="'entry-' + localPoi.id">
                        {{ __('manage_poi.alert_types.in') }}
                    </label>
                </div>
                <div class="form-check">
                    <input type="checkbox" class="form-check-input" :id="'exit-' + localPoi.id"
                        v-model="localPoi.alert.notification_events" value="exit" />
                    <label class="form-check-label" :for="'exit-' + localPoi.id">
                        {{ __('manage_poi.alert_types.out') }}
                    </label>
                </div>
            </div>
        </div>


        <golia-button v-if="localPoi.id" class="mt-3" variant="btn-success" type="button" @click="submitForm"
            :label="__('edit')" />

        <golia-button v-else class="mt-3" variant="btn-success" type="button" @click="submitForm" :disabled="!validPoi"
            :label="__('add_new')" />
    </form>
</template>

<script>
    import { mapState } from 'vuex'

    export default {
        props: {
            value: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                localPoi: this.value,
                areaTypes: [
                    { value: 'circle', text: this.__('manage_poi.fields.area_type_circle') },
                    { value: 'polygon', text: this.__('manage_poi.fields.area_type_polygon') }
                ],
                toggleWaypoint: false,
                isInitializing: true
            }
        },
        created() {
            this.localPoi.color = this.localPoi?.color || this.poiTypeColor || '#000000'

            // Initialize alert object and notification_events array if they don't exist
            if (!this.localPoi.alert) {
                this.$set(this.localPoi, 'alert', {})
            }
            if (!this.localPoi.alert.notification_events) {
                this.$set(this.localPoi.alert, 'notification_events', [])
            }

            // Set initialization flag to false after setup
            this.$nextTick(() => {
                this.isInitializing = false
            })
        },
        methods: {
            isValidPolygonGeoJson(geojson) {
                if (!geojson || geojson.type !== 'Feature') return false
                if (!geojson.geometry || geojson.geometry.type !== 'Polygon') return false

                const ring = geojson.geometry.coordinates && geojson.geometry.coordinates[0]
                if (!Array.isArray(ring) || ring.length < 4) return false

                // GeoJSON polygon ring is closed: last point equals first point.
                // We validate at least 3 distinct vertices excluding the closing point.
                const uniqueVertices = new Set(
                    ring
                        .slice(0, -1)
                        .filter((vertex) => Array.isArray(vertex) && vertex.length >= 2)
                        .map((vertex) => `${vertex[0]},${vertex[1]}`)
                )

                return uniqueVertices.size >= 3
            },
            submitForm() {
                if (
                    this.localPoi.area_type === 'polygon' &&
                    !this.isValidPolygonGeoJson(this.localPoi.polygon_geojson)
                ) {
                    const key = 'manage_poi.messages.invalid_polygon'
                    const translated = this.__(key)
                    const message =
                        translated === key
                            ? 'Invalid polygon: at least 3 vertices are required.'
                            : translated

                    if (this.$snotify && typeof this.$snotify.error === 'function') {
                        this.$snotify.error(message, { position: 'centerTop' })
                    } else {
                        alert(message)
                    }
                    return
                }

                // Handle form submission logic here
                if (this.localPoi.id) {
                    this.$emit('update', this.localPoi)
                } else {
                    this.$emit('store', this.localPoi)
                }
            }
        },
        watch: {
            value: function (val) {
                this.localPoi = val
            },
            localPoi: function (val) {
                this.$emit('input', val)
            },
            'localPoi.type': function (val) {
                this.localPoi.color = this.poiTypeColor
            },
            'localPoi.area_type': function (val) {
                if (val === 'polygon') {
                    this.localPoi.radius = null
                } else {
                    this.localPoi.polygon_geojson = null
                    if (!this.localPoi.radius) {
                        this.localPoi.radius = 100
                    }
                }
            },
            'localPoi.address': {
                handler: function (val) {
                    if (!val) return

                    if (val.referenceCoordinate) {
                        this.$emit('searchAddress', [
                            val.referenceCoordinate.y,
                            val.referenceCoordinate.x
                        ])

                        this.localPoi.lat = val.referenceCoordinate.y
                        this.localPoi.lng = val.referenceCoordinate.x

                        this.$emit('input', this.localPoi)
                    }
                },
                deep: true
            },
            'localPoi.alert.notification_events': {
                handler: function (val) {
                    // Skip if we're still initializing
                    if (this.isInitializing) return

                    if (!this.localPoi.id) return

                    if (val.length > 0) { // if at least one event is selected
                        this.$emit('handlePoiNotification', this.localPoi)
                    } else {
                        this.$emit('removePoiNotification', this.localPoi)
                    }
                }
            }
        },
        computed: {
            poiTypes() {
                return this.poiCategories.map((category) => ({
                    value: category.id,
                    text: category.description
                }))
            },
            poiTypeColor() {
                return this.poiCategories.find((category) => category.id == this.localPoi.type)?.color
            },
            validPoi() {
                const isPolygon = this.localPoi.area_type === 'polygon'
                return (
                    this.localPoi.description &&
                    ((isPolygon && this.isValidPolygonGeoJson(this.localPoi.polygon_geojson)) ||
                        (!isPolygon && this.localPoi.lat && this.localPoi.lng && this.localPoi.radius))
                )
            },
            ...mapState({
                poiCategories: (state) => state.pois.poiCategories
            })
        }
    }
</script>

<style lang="scss" scoped>
    input[type='color'].form-control {
        padding: 8px;
        border: 1px solid #d2d2d2;
        border-radius: 12px;
        height: calc(1.5em + 0.75rem);
    }
</style>
